import { createPromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { AdminService } from "schema/gen/es/chiikipoint/adminconsole/v2/service_connect";
import {
  adminServiceMockTransport,
} from "../mocks/admin";
import { debugLog } from "../debug-log";
import { isMockEnv } from "../utils";

const summarizeToken = (token: string) => ({
  hasToken: Boolean(token),
  tokenLength: token.length,
});

const createAdminTransport = ({
  token,
  tenantSlug,
}: {
  token: string,
  tenantSlug: string;
}) =>
  createConnectTransport({
    baseUrl: "/",
    interceptors: [
      (next) => async (req) => {
        debugLog("[admin-api] Request started", {
          service: req.service.typeName,
          method: req.method.name,
          tenantSlug,
          pathname: window.location.pathname,
          ...summarizeToken(token),
        });
        req.header.set("Authorization", `Bearer ${token}`);
        req.header.set("X-Tenant-Slug", tenantSlug);
        try {
          const response = await next(req);
          debugLog("[admin-api] Request succeeded", {
            service: req.service.typeName,
            method: req.method.name,
            tenantSlug,
            pathname: window.location.pathname,
          });
          return response;
        } catch (error) {
          debugLog("[admin-api] Request failed", {
            service: req.service.typeName,
            method: req.method.name,
            tenantSlug,
            pathname: window.location.pathname,
            error,
          });
          throw error;
        }
      },
    ],
    jsonOptions: {
      emitDefaultValues: true,
    },
  });

export type AdminClient = ReturnType<
  typeof createPromiseClient<typeof AdminService>
>;

export const createAdminClient = ({
  token,
  tenantSlug,
}: {
  token: string;
  tenantSlug: string;
}) => {
  const mock = isMockEnv();
  debugLog("[admin-api] Creating admin client", {
    mock,
    tenantSlug,
    pathname: window.location.pathname,
    ...summarizeToken(token),
  });
  return createPromiseClient(
    AdminService,
    mock ? adminServiceMockTransport : createAdminTransport({ token, tenantSlug }),
  );
};
